import { Inject, Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { RidangoReduxAction } from '@ridango/core';

import { Observable } from 'rxjs';

import { ApplicationActions } from './application.actions';
import * as ApplicationSelectors from './application.selectors';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class ApplicationFacade {
    assetsPath$: Observable<string> = this.store.select(ApplicationSelectors.getAssetsPath);
    highContrast$: Observable<boolean> = this.store.select(ApplicationSelectors.getHighContrast);
    termsUrl$: Observable<string> = this.store.select(ApplicationSelectors.getTermsUrl);
    ready$: Observable<boolean> = this.store.select(ApplicationSelectors.getReady);
    private mobileMenuOpen = false;

    constructor(
        @Inject('ENV') public env: Env,
        private store: NgRedux<any>,
        private actions: ApplicationActions,
        private translate: TranslateService
    ) {
        this.initApplicationFacade();
    }

    dispatch(action: RidangoReduxAction) {
        this.store.dispatch(action);
    }

    initApplicationFacade() {
        if (this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE && window.location.href.includes('/mobile-menu')) {
            this.setMobileMenuOpen(true);
        }
        this.store
            .select((state) => state.core.selectedLanguage?.code)
            .pipe(
                filter((lang): lang is string => !!lang),
                withLatestFrom(
                    this.store.select((state) => state.core.region?.terms),
                    this.store.select((state) => state.application?.highContrast)
                ),
                filter(([terms]) => !!terms), // Also make sure terms are loaded
                map(([lang, terms, highContrast]) => {
                    const termsBaseUrl = this.env.ENABLE_TERMS_WMB_VERSION
                        ? terms?.contentBaseUrl
                        : terms?.content_base_url;
                    return `${termsBaseUrl}/${lang}/${highContrast ? '#high-contrast' : ''}`;
                })
            )
            .subscribe((termsUrl) => {
                this.dispatch(this.actions.setTermsUrl(termsUrl));
            });
    }

    setPeriodTicketProgress(progress: number) {
        this.dispatch(this.actions.setProgress(progress));
    }

    setRecoverProgress(progress: number) {
        this.dispatch(this.actions.setRecoverProgress(progress));
    }

    setRegistrationProgress(progress: number) {
        this.dispatch(this.actions.setRegistrationProgress(progress));
    }

    setWalletLoadingProgress(progress: number) {
        this.dispatch(this.actions.setWalletLoadingProgress(progress));
    }

    setLoggedInCustomer(payload) {
        this.dispatch(this.actions.setLoggedInCustomer(payload));
    }

    setPageTitle(title: string) {
        this.dispatch(this.actions.setPageTitle(title));
    }

    switchHighContrastMode() {
        this.dispatch(this.actions.switchHighContrastMode());
        this.updateTermsUrl();
    }

    setAssetsPath(path: string) {
        this.dispatch(this.actions.setAssetsPath(path));
    }

    updateTermsUrl(languageCode?: string) {
        const terms = this.getTerms();
        const highContrast = this.store.getState().application.highContrast;
        const selectedLanguage = languageCode || this.translate.currentLang;
        let termsUrl: string;

        if (this.env.ENABLE_TERMS_WMB_VERSION) {
            termsUrl = `${terms?.contentBaseUrl}/${selectedLanguage}/`;
        } else {
            termsUrl = `${terms?.content_base_url}/${selectedLanguage}/`;
        }

        termsUrl = highContrast ? termsUrl + '#high-contrast' : termsUrl;

        this.dispatch(this.actions.setTermsUrl(termsUrl));
    }

    getTerms() {
        return this.store.getState().core.region.terms;
    }

    isMenuOpen() {
        return this.mobileMenuOpen;
    }

    setMobileMenuOpen(open: boolean) {
        this.mobileMenuOpen = open;
    }

    setReady(ready: boolean) {
        this.dispatch(this.actions.setReady(ready));
    }

    setUserAgreedTermsVersionOnOnboarding(version: string) {
        this.dispatch(this.actions.setUserAgreedTermsVersionOnOnboarding(version));
    }
}
